import React, { useState } from 'react';
import { Input } from 'reactstrap';
import ViewOriginModal from '../../../../Procurement/component/ViewOriginModal';
import { COUNTER_ACCENT } from '../../../../../shared/offer-domain/constants';
import {
  formatPortLocation,
  formatParcelSize,
  formatPortDateRange,
  formatFobLocationsJoined,
  isFobOriginDisplay,
} from './utils';

export const cellClass = 'px-2 py-2 align-top';
export const headerCellClass =
  'px-1 py-2 fs-11 fw-semibold text-center align-middle';

/** FOB Straits: loc1 or loc2. FOB Origin: All Origin + Show All modal. */
export const FobLocationLabel = ({
  names = [],
  fallback = null,
  route = null,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const list = (Array.isArray(names) ? names : []).filter(Boolean);
  const fallbackText = formatPortLocation(fallback);

  if (!list.length) {
    return <span className='fw-medium'>{fallbackText}</span>;
  }

  const isOrigin = isFobOriginDisplay(route, list);
  const label = isOrigin ? 'All Origin' : formatFobLocationsJoined(list);

  return (
    <>
      <span className='fw-medium'>
        {label}
        {isOrigin ? (
          <>
            {' '}
            <button
              type='button'
              className='btn btn-link btn-sm p-0 align-baseline text-primary fw-medium'
              style={{ fontSize: 'inherit' }}
              onClick={() => setModalOpen(true)}
            >
              Show All
            </button>
          </>
        ) : null}
      </span>
      {isOrigin ? (
        <ViewOriginModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          locations={list.map(name => ({ name }))}
        />
      ) : null}
    </>
  );
};

export const renderSelectedOriginLocations = (names = [], fallback = '-') => {
  const list = (Array.isArray(names) ? names : []).filter(Boolean);
  if (!list.length) return fallback;
  return (
    <span className='d-inline-flex flex-column'>
      {list.map((name, i) => (
        <span key={`${name}-${i}`}>{name}</span>
      ))}
    </span>
  );
};

export const renderPortCell = (
  port,
  parcelSize,
  {
    portLabel = 'Port',
    startDate = null,
    endDate = null,
    fallbackDate = null,
    highlight = {},
    unitLabel = 'MB',
    locationNames = null,
    route = null,
  } = {}
) => {
  const parcelText = formatParcelSize(parcelSize, unitLabel);
  const dateText = formatPortDateRange(startDate, endDate, fallbackDate);
  const locationText = formatPortLocation(port);
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;
  const hasParcelSize =
    parcelSize !== null &&
    parcelSize !== undefined &&
    String(parcelSize).trim() !== '' &&
    parcelText !== `- ${unitLabel}`;

  if (
    locationText === '-' &&
    !hasLocationNames &&
    !hasParcelSize &&
    dateText === '-'
  ) {
    return <span className='text-muted fs-12'>-</span>;
  }

  return (
    <div
      className='fs-12 lh-base d-flex flex-column gap-1'
      style={{
        minWidth: 0,
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
      }}
    >
      <span
        className={`${highlight.port ? 'fw-semibold' : 'fw-medium'}`}
        style={highlight.port ? { color: COUNTER_ACCENT } : undefined}
      >
        {portLabel} :{' '}
        {hasLocationNames ? (
          <FobLocationLabel
            names={locationNames}
            fallback={port}
            route={route}
          />
        ) : (
          locationText
        )}
      </span>
      <span
        className={`${highlight.parcelSize ? 'fw-semibold' : 'text-muted'}`}
        style={highlight.parcelSize ? { color: COUNTER_ACCENT } : undefined}
      >
        Parcel Size : {parcelText}
      </span>
      <span
        className={`${highlight.date ? 'fw-semibold' : 'text-muted'}`}
        style={highlight.date ? { color: COUNTER_ACCENT } : undefined}
      >
        Date : {dateText}
      </span>
    </div>
  );
};

/** Port cell for LPG offer table (Location + multi parcel sizes + ALD/ADD). */
export const renderLpgPortCell = (
  port,
  parcelSizes = [],
  {
    startDate = null,
    endDate = null,
    fallbackDate = null,
    locationNames = null,
    route = null,
    highlight = {},
  } = {}
) => {
  const locationText = formatPortLocation(port);
  const dateText = formatPortDateRange(startDate, endDate, fallbackDate);
  const sizes = (
    Array.isArray(parcelSizes) ? parcelSizes : parcelSizes ? [parcelSizes] : []
  ).filter(size => {
    const text = String(size ?? '').trim();
    return text && text !== '-' && !text.startsWith('-');
  });
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;

  if (
    locationText === '-' &&
    !hasLocationNames &&
    !sizes.length &&
    dateText === '-'
  ) {
    return <span className='text-muted fs-13'>-</span>;
  }

  return (
    <div
      className='fs-12 lh-sm d-flex flex-column'
      style={{
        gap: '6px',
        minWidth: 0,
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
      }}
    >
      <div className='d-flex flex-column'>
        <span className='fw-semibold text-nowrap'>Location</span>
        <span
          className={`${highlight.port ? 'fw-semibold' : 'fw-medium'} text-break`}
          style={highlight.port ? { color: COUNTER_ACCENT } : undefined}
        >
          {hasLocationNames ? (
            <FobLocationLabel
              names={locationNames}
              fallback={port}
              route={route}
            />
          ) : (
            locationText
          )}
        </span>
      </div>
      <div className='d-flex flex-column'>
        <span className='fw-semibold text-nowrap'>Parcel Size</span>
        <div
          className={`d-flex flex-column ${highlight.parcelSize ? 'fw-semibold' : 'text-muted'}`}
          style={{
            gap: '1px',
            color: highlight.parcelSize ? COUNTER_ACCENT : undefined,
          }}
        >
          {sizes.length > 0 ? (
            sizes.map((size, index) => (
              <span key={`${size}-${index}`} className='text-break'>
                {size}
              </span>
            ))
          ) : (
            <span>-</span>
          )}
        </div>
      </div>
      <div className='d-flex flex-column'>
        <span className='fw-semibold text-nowrap'>ALD/ADD</span>
        <span
          className={`text-break ${highlight.date ? 'fw-semibold' : 'text-muted'}`}
          style={highlight.date ? { color: COUNTER_ACCENT } : undefined}
        >
          {dateText}
        </span>
      </div>
    </div>
  );
};

export const createNumberInputRenderer = ({
  isDeliveryEditable,
  handleInputChange,
}) => {
  const NumberInputRenderer = ({
    rowKey,
    row,
    field,
    value,
    disabled,
    suffix,
    placeholder,
    required = false,
    invalid = false,
  }) => (
    <div
      className='d-flex flex-column gap-1'
      style={{ minWidth: 0, width: '100%', maxWidth: '100%' }}
    >
      <Input
        type='number'
        disabled={!isDeliveryEditable || disabled}
        className={`form-control${invalid ? ' is-invalid' : ''}`}
        style={{ height: '34px', width: '100%', minWidth: 0 }}
        placeholder={placeholder || (required ? 'Required' : undefined)}
        value={value ?? ''}
        readOnly={!isDeliveryEditable}
        required={required}
        invalid={invalid}
        onChange={e => handleInputChange(e, rowKey, field, 'number', row)}
      />
      {invalid && (
        <span className='text-danger fs-12'>Alpha pricing is required</span>
      )}
      {!invalid && suffix && (
        <span className='text-muted fs-12 text-truncate' title={suffix}>
          {suffix}
        </span>
      )}
    </div>
  );
  NumberInputRenderer.displayName = 'NumberInputRenderer';
  return NumberInputRenderer;
};
